/**
 * Driver licence and passport number rules from Equifax Appendix B (ADO 1633).
 * Applied on the origination form so Equifax is not the first place an invalid number fails.
 */

/** Victoria shows 9-digit licences as 10 digits; Equifax wants the leading zero dropped. */
export const normalizeDriversLicenceNumber = (value: string | null | undefined): string => {
  const trimmed = (value ?? '').trim();
  return /^0\d{9}$/.test(trimmed) ? trimmed.slice(1) : trimmed;
};

export type DriversLicenceNumberError =
  | 'driversLicenceCharset'
  | 'driversLicenceLength'
  | 'driversLicenceNumericCount'
  | 'driversLicenceAlphaCount'
  | 'driversLicenceThirdFourth';

export const driversLicenceNumberError = (
  value: string | null | undefined,
): DriversLicenceNumberError | null => {
  const v = normalizeDriversLicenceNumber(value);
  if (!v) {
    return null;
  }
  if (!/^[A-Za-z0-9]+$/.test(v)) {
    return 'driversLicenceCharset';
  }
  if (v.length < 4 || v.length > 9) {
    return 'driversLicenceLength';
  }
  const numeric = (v.match(/[0-9]/g) ?? []).length;
  const alpha = (v.match(/[A-Za-z]/g) ?? []).length;
  if (numeric < 4) {
    return 'driversLicenceNumericCount';
  }
  if (alpha > 2) {
    return 'driversLicenceAlphaCount';
  }
  if (!/[0-9]/.test(v[2]) || !/[0-9]/.test(v[3])) {
    return 'driversLicenceThirdFourth';
  }
  return null;
};

export type PassportNumberError = 'passportCharset';

export const passportNumberError = (
  value: string | null | undefined,
): PassportNumberError | null => {
  const v = (value ?? '').trim();
  if (!v) {
    return null;
  }
  if (!/^[A-Za-z0-9]+$/.test(v)) {
    return 'passportCharset';
  }
  return null;
};
